import type { Emphasis, Link, Paragraph, Root, RootContent } from 'mdast';
import type { Plugin } from 'unified';
import { stripCitationMarkers } from './chat-citation-markers';
import type { WebCitationSource } from './chat-web-citations';

type MarkdownNode = Root | RootContent;

/** Rendered by `CitationBadge`; the text child carries the cited keys. */
export const CITATION_BADGE_TAG = 'cite';

export type CitationBadgeOptions = {
  /** Keys of the document passages that can be opened (`D#`). */
  documentKeys: readonly string[];
  webSources: readonly WebCitationSource[];
};

type KnownKeys = {
  document: ReadonlySet<string>;
  web: ReadonlySet<string>;
};

type CitedKeys = { document: string[]; web: string[] };

function addUnique(target: string[], keys: readonly string[]) {
  for (const key of keys) {
    if (!target.includes(key)) target.push(key);
  }
}

function stripMarkers(node: MarkdownNode, known: KnownKeys, cited: CitedKeys) {
  if (node.type === 'text') {
    const result = stripCitationMarkers(node.value, known.web, known.document);
    node.value = result.text;
    addUnique(cited.web, result.webKeys);
    addUnique(cited.document, result.documentKeys);
    return;
  }
  if ('children' in node) {
    for (const child of node.children) stripMarkers(child, known, cited);
  }
}

function findLastParagraph(node: MarkdownNode): Paragraph | null {
  if (node.type === 'paragraph') return node;
  if (!('children' in node)) return null;
  for (let index = node.children.length - 1; index >= 0; index -= 1) {
    const paragraph = findLastParagraph(node.children[index]);
    if (paragraph) return paragraph;
  }
  return null;
}

function webBadge(keys: string[], sourcesByKey: ReadonlyMap<string, WebCitationSource>): Link | null {
  const primary = sourcesByKey.get(keys[0]);
  return primary
    ? {
      type: 'link',
      url: primary.url,
      children: [{ type: 'text', value: keys.join(' ') }],
      data: { hName: CITATION_BADGE_TAG, hProperties: { title: primary.url } },
    }
    : null;
}

function documentBadge(keys: string[]): Emphasis {
  return {
    type: 'emphasis',
    children: [{ type: 'text', value: keys.join(' ') }],
    data: { hName: CITATION_BADGE_TAG },
  };
}

function buildBadges(cited: CitedKeys, sourcesByKey: ReadonlyMap<string, WebCitationSource>) {
  const badges: Array<Emphasis | Link> = [];
  const web = cited.web.length > 0 ? webBadge(cited.web, sourcesByKey) : null;
  if (web) badges.push(web);
  if (cited.document.length > 0) badges.push(documentBadge(cited.document));
  return badges;
}

/** Returns the paragraph to insert after a cited table, if any. */
function attachBadges(
  block: RootContent,
  known: KnownKeys,
  sourcesByKey: ReadonlyMap<string, WebCitationSource>,
): Paragraph | null {
  if (block.type === 'blockquote') {
    for (const child of block.children) attachBadges(child, known, sourcesByKey);
    return null;
  }

  const cited: CitedKeys = { document: [], web: [] };
  stripMarkers(block, known, cited);
  if (block.type === 'table') {
    // A table cell cannot hold the badge without breaking the grid: the badge
    // goes in its own paragraph right under the table.
    const badges = buildBadges(cited, sourcesByKey);
    return badges.length > 0 ? { type: 'paragraph', children: badges } : null;
  }
  if (block.type !== 'paragraph' && block.type !== 'list') {
    return null;
  }

  const target = findLastParagraph(block);
  target?.children.push(...buildBadges(cited, sourcesByKey));
  return null;
}

/**
 * Removes `[S#]` and `[D#]` markers and keeps citations occasional: at most one
 * site badge and one document badge per paragraph, list or table, at the end of
 * the block (under a table). Headings lose their markers without receiving a
 * badge; inline code is never touched.
 */
export const remarkCitationBadges: Plugin<[CitationBadgeOptions], Root> = ({ documentKeys, webSources }) => (tree) => {
  const sourcesByKey = new Map(webSources.map((source) => [source.citationKey, source]));
  const known: KnownKeys = { document: new Set(documentKeys), web: new Set(sourcesByKey.keys()) };
  tree.children = tree.children.flatMap((block) => {
    const trailingBadges = attachBadges(block, known, sourcesByKey);
    return trailingBadges ? [block, trailingBadges] : [block];
  });
};
